import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useToast } from "./ToastContext";
import { useSettings } from "./SettingsContext";
import { getBrowserApiBase } from "../lib/browser-api-base";

export type GatewayStatus =
  "connected" | "connecting" | "disconnected" | "error";
export type InteractiveRuntimeStatus = "ok" | "degraded" | "error" | "unknown";

interface HealthResponse {
  status?: string;
  components?: {
    aiGateway?: { status?: string };
  };
}

interface GatewayContextValue {
  status: GatewayStatus;
  runtimeStatus: InteractiveRuntimeStatus;
  lastHealthCheck: string | null;
  reconnect: () => void;
  gatewayUrl: string;
}

interface CachedHealth {
  status: GatewayStatus;
  runtimeStatus: InteractiveRuntimeStatus;
  lastHealthCheck: string;
}

const GatewayContext = createContext<GatewayContextValue>({
  status: "disconnected",
  runtimeStatus: "unknown",
  lastHealthCheck: null,
  reconnect: () => {},
  gatewayUrl: "http://localhost:8080",
});

const DEFAULT_GATEWAY_PORT = 8080;
const HEALTH_INTERVAL_MS = 30_000;
const HEALTH_TIMEOUT_MS = 12_000;
const MAX_BACKOFF_MS = 60_000;
const CACHE_KEY = "fintheon:gatewayHealth";
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

export function GatewayProvider({ children }: { children: ReactNode }) {
  const { gatewayPort } = useSettings();
  const cachedHealth = loadCachedHealth();
  const [status, setStatus] = useState<GatewayStatus>(
    cachedHealth?.status ?? "connecting",
  );
  const [runtimeStatus, setRuntimeStatus] = useState<InteractiveRuntimeStatus>(
    cachedHealth?.runtimeStatus ?? "unknown",
  );
  const [lastHealthCheck, setLastHealthCheck] = useState<string | null>(
    cachedHealth?.lastHealthCheck ?? null,
  );
  const statusRef = useRef(status);
  const backoffRef = useRef(2_000);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkInFlightRef = useRef(false);
  const connectingToastRef = useRef<string | null>(null);
  const { addToast, dismissToast } = useToast();
  const gatewayUrl = getBrowserApiBase(
    import.meta.env.VITE_GATEWAY_URL ||
      `http://localhost:${gatewayPort || DEFAULT_GATEWAY_PORT}`,
  );
  const healthBaseUrl =
    gatewayUrl || String(import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

  const checkHealth = useCallback(async () => {
    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;
    try {
      const response = await fetch(`${healthBaseUrl}/healthz`, {
        signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
      });
      const contentType = response.headers.get("content-type") ?? "";
      if (!response.ok || !contentType.includes("application/json")) {
        throw new Error(`HTTP ${response.status}`);
      }

      const body = (await response.json()) as HealthResponse;
      const wasDisconnected = statusRef.current !== "connected";
      const checkedAt = new Date().toISOString();
      const nextRuntimeStatus = normalizeRuntimeStatus(
        body.components?.aiGateway?.status,
      );

      setStatus("connected");
      statusRef.current = "connected";
      setRuntimeStatus(nextRuntimeStatus);
      setLastHealthCheck(checkedAt);
      backoffRef.current = 2_000;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      persistHealth("connected", nextRuntimeStatus, checkedAt);

      if (wasDisconnected) {
        if (connectingToastRef.current) {
          dismissToast(connectingToastRef.current);
          connectingToastRef.current = null;
        }
        if (!sessionStorage.getItem("gateway_connected_shown")) {
          sessionStorage.setItem("gateway_connected_shown", "1");
          addToast(
            "Fintheon backend connected",
            "success",
            undefined,
            "connection-status",
          );
        }
      }
    } catch {
      const wasConnected = statusRef.current === "connected";
      setStatus("disconnected");
      statusRef.current = "disconnected";
      setRuntimeStatus("unknown");

      if (wasConnected) {
        addToast(
          "Fintheon cloud unreachable - retrying...",
          "error",
          "Check your network. Durable runs recover when the backend reconnects.",
          "connection-status",
        );
      }

      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
      if (!retryTimerRef.current) {
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          void checkHealth();
        }, backoffRef.current);
      }
    } finally {
      checkInFlightRef.current = false;
    }
  }, [addToast, dismissToast, healthBaseUrl]);

  const reconnect = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    setStatus("connecting");
    statusRef.current = "connecting";
    setRuntimeStatus("unknown");
    connectingToastRef.current = addToast(
      "Reconnecting to Fintheon...",
      "updating",
      undefined,
      "connection-status",
    );
    backoffRef.current = 2_000;
    void checkHealth();
  }, [addToast, checkHealth]);

  useEffect(() => {
    setStatus("connecting");
    statusRef.current = "connecting";
    setRuntimeStatus("unknown");
    backoffRef.current = 2_000;
    void checkHealth();
    const interval = setInterval(checkHealth, HEALTH_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [checkHealth, gatewayUrl]);

  return (
    <GatewayContext.Provider
      value={{
        status,
        runtimeStatus,
        lastHealthCheck,
        reconnect,
        gatewayUrl,
      }}
    >
      {children}
    </GatewayContext.Provider>
  );
}

export const useGateway = () => useContext(GatewayContext);

function normalizeRuntimeStatus(value: unknown): InteractiveRuntimeStatus {
  if (value === "ok") return "ok";
  if (value === "degraded") return "degraded";
  if (value === "error") return "error";
  return "unknown";
}

function loadCachedHealth(): CachedHealth | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as Partial<CachedHealth>;
    const lastMs = new Date(cached.lastHealthCheck ?? "").getTime();
    if (!Number.isFinite(lastMs) || Date.now() - lastMs > CACHE_MAX_AGE_MS) {
      return null;
    }
    if (
      !isGatewayStatus(cached.status) ||
      !isRuntimeStatus(cached.runtimeStatus)
    ) {
      return null;
    }
    return cached as CachedHealth;
  } catch {
    return null;
  }
}

function persistHealth(
  status: GatewayStatus,
  runtimeStatus: InteractiveRuntimeStatus,
  lastHealthCheck: string,
) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ status, runtimeStatus, lastHealthCheck }),
    );
  } catch {
    // Cache is best-effort.
  }
}

function isGatewayStatus(value: unknown): value is GatewayStatus {
  return ["connected", "connecting", "disconnected", "error"].includes(
    String(value),
  );
}

function isRuntimeStatus(value: unknown): value is InteractiveRuntimeStatus {
  return ["ok", "degraded", "error", "unknown"].includes(String(value));
}
